import {Suspense, useEffect, useState} from 'react'
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {Outlet} from 'react-router-dom'
import {Toaster} from 'sonner'
import Footer from '../components/footer'
import Header from '../components/header'
import Loader from '../components/Loader'
import {useInitSession} from '../hooks/auth.hooks'
import {useAmplitudeUserSync, usePageTracking} from '../hooks/amplitude.hooks'
import ProtectedRoute from './ProtectedRoute'
import './styles/root.css'
import {TypeOutletContext} from '../types/auth.type'

import {defaultQueryClientOptions, setQueryClient} from '../lib/queryClient'

const queryClient = new QueryClient({defaultOptions: defaultQueryClientOptions})

interface AppLoaderProps {
  viewType: string
}

/**
 * Inner component rendered inside QueryClientProvider.
 * Handles session re-hydration after a page refresh before rendering the app.
 */
function AppLoader({viewType}: AppLoaderProps) {
  const {isLoadingSession} = useInitSession()

  useAmplitudeUserSync()
  usePageTracking(isLoadingSession)

  if (isLoadingSession) {
    // Brief loading window while re-fetching the user profile.
    // The fetcher silently refreshes the access token if needed.
    return <Loader variant='fullscreen' />
  }

  return (
    <>
      <Toaster position='top-center' />
      <div className='RootContainer'>
        <Header />
        <div className='RootContentContainer'>
          <Suspense fallback={<Loader variant='fullscreen' />}>
            <Outlet context={{viewType} satisfies TypeOutletContext} />
          </Suspense>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default function Root() {
  const [viewType] = useState(
    'ope',
    // 'setting',
  )

  useEffect(() => {
    setQueryClient(queryClient)
  }, [queryClient])

  return (
    <ProtectedRoute>
      <QueryClientProvider client={queryClient}>
        <AppLoader viewType={viewType} />
      </QueryClientProvider>
    </ProtectedRoute>
  )
}
